import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from 'lucide-react';

import { InspectionImageType } from '@/enums/InspectionImage.enum';
import { imageStorage } from '@/store/idbStorage/imageStorage';
import { Image } from '@/types/Image.type';

import styles from './ImageZoomLightbox.module.scss';

const MOVE_THRESHOLD_PX = 8;
const SWIPE_CAROUSEL_PX = 48;
const MIN_SCALE = 1;
const MAX_SCALE = 4;
const ZOOM_STEP_CLICK = 2;
const ZOOM_BUTTON_FACTOR = 1.35;
const WHEEL_SCALE_SENSITIVITY = 0.0015;
const TRANSITION_MS = 220;

export interface ImageZoomSlide {
    image: Image;
    typeLabel: string;
}

/** Images for this inspection grouped by type label (e.g. template image categories). */
export interface InspectionImageCategoryGroup {
    typeLabel: string;
    inspectionImageType: InspectionImageType;
    images: Image[];
}

export interface ImageZoomLightboxProps {
    inspectionId: string;
    inspectionImageType: InspectionImageType;
    slides: ImageZoomSlide[];
    initialIndex: number;
    onClose: () => void;
}

function clamp(n: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, n));
}

export const ImageZoomLightbox: React.FC<ImageZoomLightboxProps> = ({
    inspectionId,
    inspectionImageType,
    slides,
    initialIndex,
    onClose,
}) => {
    const { t } = useTranslation();
    const viewportRef = useRef<HTMLDivElement>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);

    const [activeIndex, setActiveIndex] = useState(() =>
        clamp(initialIndex, 0, Math.max(0, slides.length - 1)),
    );

    const [resolvedSrc, setResolvedSrc] = useState<string | undefined>(undefined);
    const [scale, setScale] = useState(MIN_SCALE);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);
    const [fitted, setFitted] = useState({ w: 0, h: 0 });
    const [scaleTransition, setScaleTransition] = useState(true);
    const [isGrabbing, setIsGrabbing] = useState(false);

    const pointersRef = useRef(new Map<number, { x: number; y: number }>());

    const pinchRef = useRef<{
        distance: number;
        startScale: number;
        startTx: number;
        startTy: number;
    } | null>(null);

    const singleRef = useRef<{
        id: number;
        startX: number;
        startY: number;
        startTx: number;
        startTy: number;
        moved: boolean;
        panning: boolean;
    } | null>(null);

    const pinchGestureRef = useRef(false);

    const currentSlide = slides[activeIndex];
    const currentImage = currentSlide?.image;
    const typeLabel = currentSlide?.typeLabel?.trim() || t('Images');
    const alt = currentImage?.name || currentImage?.id || '';
    const slideCount = slides.length;
    const canCarousel = slideCount > 1;

    const clampPan = useCallback(
        (s: number, nx: number, ny: number, fw: number, fh: number) => {
            const vp = viewportRef.current;
            if (!vp || fw <= 0 || fh <= 0) return { x: nx, y: ny };
            const vw = vp.clientWidth;
            const vh = vp.clientHeight;
            const sw = fw * s;
            const sh = fh * s;
            const maxX = Math.max(0, (sw - vw) / 2);
            const maxY = Math.max(0, (sh - vh) / 2);
            return {
                x: clamp(nx, -maxX, maxX),
                y: clamp(ny, -maxY, maxY),
            };
        },
        [],
    );

    const recalcFittedFromImage = useCallback(() => {
        const img = imgRef.current;
        const vp = viewportRef.current;
        if (!img || !vp || !img.naturalWidth) return;
        const rect = img.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
            setFitted({ w: rect.width, h: rect.height });
        }
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!currentImage) {
            setResolvedSrc(undefined);
            return;
        }
        const im = currentImage;
        const storageT = im.type ?? inspectionImageType;
        const apply = (s?: string) => {
            if (!cancelled) setResolvedSrc(s);
        };
        if (im.url?.trim()) apply(im.url);
        else if (im.uploadedUrl?.trim()) apply(im.uploadedUrl);
        else if (im.data) apply(im.data as string);
        else {
            setResolvedSrc(undefined);
            imageStorage.getImage(inspectionId, storageT, im.id).then((data) => {
                if (!cancelled) apply(data ?? undefined);
            });
        }
        return () => {
            cancelled = true;
        };
    }, [
        activeIndex,
        currentImage?.id,
        currentImage?.url,
        currentImage?.uploadedUrl,
        currentImage?.data,
        currentImage?.type,
        inspectionId,
        inspectionImageType,
    ]);

    useLayoutEffect(() => {
        if (resolvedSrc) recalcFittedFromImage();
    }, [resolvedSrc, activeIndex, recalcFittedFromImage]);

    useEffect(() => {
        if (!currentSlide) return;
        setScale(MIN_SCALE);
        setTx(0);
        setTy(0);
    }, [activeIndex, currentSlide?.image.id]);

    useEffect(() => {
        const onResize = () => {
            setScale(MIN_SCALE);
            setTx(0);
            setTy(0);
            requestAnimationFrame(() => recalcFittedFromImage());
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [recalcFittedFromImage]);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, []);

    const goPrev = useCallback(() => {
        setScaleTransition(true);
        setActiveIndex((i) => (i <= 0 ? slideCount - 1 : i - 1));
    }, [slideCount]);

    const goNext = useCallback(() => {
        setScaleTransition(true);
        setActiveIndex((i) => (i >= slideCount - 1 ? 0 : i + 1));
    }, [slideCount]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
                return;
            }
            if (!canCarousel) return;
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                goPrev();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                goNext();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, canCarousel, goPrev, goNext]);

    const applyPan = useCallback(
        (nx: number, ny: number) => {
            const next = clampPan(scale, nx, ny, fitted.w, fitted.h);
            setTx(next.x);
            setTy(next.y);
        },
        [clampPan, scale, fitted.w, fitted.h],
    );

    const clampAfterScale = useCallback(
        (nextScale: number, nx: number, ny: number) => {
            const s = clamp(nextScale, MIN_SCALE, MAX_SCALE);
            setScale(s);
            const next = clampPan(s, nx, ny, fitted.w, fitted.h);
            setTx(next.x);
            setTy(next.y);
        },
        [clampPan, fitted.w, fitted.h],
    );

    const toggleZoomTap = useCallback(() => {
        setScaleTransition(true);
        if (scale <= MIN_SCALE + 0.01) {
            const s = clamp(ZOOM_STEP_CLICK, MIN_SCALE, MAX_SCALE);
            setScale(s);
            setTx(0);
            setTy(0);
        } else {
            setScale(MIN_SCALE);
            setTx(0);
            setTy(0);
        }
    }, [scale]);

    const onZoomInClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setScaleTransition(true);
        clampAfterScale(scale * ZOOM_BUTTON_FACTOR, tx, ty);
    };

    const onZoomOutClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        setScaleTransition(true);
        clampAfterScale(scale / ZOOM_BUTTON_FACTOR, tx, ty);
    };

    const onImageLoad = () => {
        requestAnimationFrame(() => recalcFittedFromImage());
    };

    const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
        Math.hypot(b.x - a.x, b.y - a.y);

    const tryCapture = (id: number) => {
        const stage = stageRef.current;
        if (!stage) return;
        try {
            stage.setPointerCapture(id);
        } catch {
            // ignore
        }
    };

    const releaseCaptureSafe = (id: number | null) => {
        const stage = stageRef.current;
        if (!stage || id === null) return;
        try {
            stage.releasePointerCapture(id);
        } catch {
            // ignore
        }
    };

    const onPointerDown = (e: React.PointerEvent) => {
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (pointersRef.current.size === 2) {
            pinchGestureRef.current = true;
            const pts = [...pointersRef.current.values()];
            pinchRef.current = {
                distance: dist(pts[0], pts[1]),
                startScale: scale,
                startTx: tx,
                startTy: ty,
            };
            singleRef.current = null;
            setScaleTransition(false);
            return;
        }

        singleRef.current = {
            id: e.pointerId,
            startX: e.clientX,
            startY: e.clientY,
            startTx: tx,
            startTy: ty,
            moved: false,
            panning: false,
        };
    };

    const onPointerMove = (e: React.PointerEvent) => {
        if (!pointersRef.current.has(e.pointerId)) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

        if (pointersRef.current.size === 2 && pinchRef.current) {
            const pts = [...pointersRef.current.values()];
            const d0 = pinchRef.current.distance;
            const d1 = dist(pts[0], pts[1]);
            if (d0 > 1) {
                const ratio = d1 / d0;
                const newScale = clamp(pinchRef.current.startScale * ratio, MIN_SCALE, MAX_SCALE);
                setScale(newScale);
                const next = clampPan(
                    newScale,
                    pinchRef.current.startTx,
                    pinchRef.current.startTy,
                    fitted.w,
                    fitted.h,
                );
                setTx(next.x);
                setTy(next.y);
            }
            setScaleTransition(false);
            return;
        }

        const g = singleRef.current;
        if (!g || g.id !== e.pointerId) return;

        const dx = e.clientX - g.startX;
        const dy = e.clientY - g.startY;
        const travel = Math.hypot(dx, dy);

        if (!g.moved && travel >= MOVE_THRESHOLD_PX) {
            g.moved = true;
            if (scale > MIN_SCALE + 0.01) {
                g.panning = true;
                tryCapture(e.pointerId);
                setIsGrabbing(true);
                setScaleTransition(false);
            }
        }

        if (g.panning) {
            applyPan(g.startTx + dx, g.startTy + dy);
        }
    };

    const onPointerUp = (e: React.PointerEvent) => {
        pointersRef.current.delete(e.pointerId);

        if (pointersRef.current.size === 1) {
            pinchRef.current = null;
            const left = [...pointersRef.current.entries()][0];
            if (left) {
                const [pid, pt] = left;
                singleRef.current = {
                    id: pid,
                    startX: pt.x,
                    startY: pt.y,
                    startTx: tx,
                    startTy: ty,
                    moved: false,
                    panning: false,
                };
            }
            return;
        }

        if (pointersRef.current.size === 0) {
            const g = singleRef.current;
            const hadPinch = pinchGestureRef.current;
            pinchRef.current = null;
            pinchGestureRef.current = false;

            if (g) {
                releaseCaptureSafe(g.id);
            }
            setIsGrabbing(false);

            const dx = g ? e.clientX - g.startX : 0;
            const dy = g ? e.clientY - g.startY : 0;
            const swipeCarousel =
                canCarousel &&
                scale <= MIN_SCALE + 0.01 &&
                g &&
                g.moved &&
                Math.abs(dx) >= SWIPE_CAROUSEL_PX &&
                Math.abs(dx) > Math.abs(dy) * 1.15 &&
                !hadPinch;

            if (swipeCarousel) {
                if (dx < 0) goNext();
                else goPrev();
            } else {
                const tap =
                    g &&
                    g.id === e.pointerId &&
                    !g.panning &&
                    !g.moved &&
                    !hadPinch;

                if (tap) {
                    toggleZoomTap();
                }
            }

            singleRef.current = null;
        }
    };

    const onPointerCancel = (e: React.PointerEvent) => {
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2) {
            pinchRef.current = null;
        }
        if (pointersRef.current.size === 0) {
            pinchGestureRef.current = false;
            releaseCaptureSafe(singleRef.current?.id ?? null);
            singleRef.current = null;
            setIsGrabbing(false);
        }
    };

    const onWheel = (e: React.WheelEvent) => {
        if (!viewportRef.current) return;
        e.preventDefault();
        setScaleTransition(false);
        const delta = -e.deltaY * WHEEL_SCALE_SENSITIVITY;
        const next = clamp(scale * (1 + delta), MIN_SCALE, MAX_SCALE);
        setScale(next);
        const panned = clampPan(next, tx, ty, fitted.w, fitted.h);
        setTx(panned.x);
        setTy(panned.y);
    };

    const translateStyle: React.CSSProperties = {
        transform: `translate(${tx}px, ${ty}px)`,
    };

    const scaleStyle: React.CSSProperties = {
        transform: `scale(${scale})`,
        transition: scaleTransition ? `transform ${TRANSITION_MS}ms cubic-bezier(0.25, 0.8, 0.25, 1)` : 'none',
    };

    const atMinZoom = scale <= MIN_SCALE + 0.02;
    const atMaxZoom = scale >= MAX_SCALE - 0.02;

    return createPortal(
        <div
            className={styles.backdrop}
            role="dialog"
            aria-modal="true"
            aria-label={alt}
            onClick={onClose}
        >
            <div
                className={styles.viewport}
                ref={viewportRef}
                onClick={(ev) => ev.stopPropagation()}
            >
                <div className={styles.topBar}>
                    <div className={styles.topBarSpacer} aria-hidden />
                    <div className={styles.topBarCenter}>
                        <div className={styles.typeLabelBar}>
                            <span className={styles.typeLabelText}>{typeLabel}</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onClose}
                        aria-label={t('close')}
                    >
                        &times;
                    </button>
                </div>

                <div className={styles.zoomRail}>
                    <button
                        type="button"
                        className={styles.zoomRailButton}
                        onClick={onZoomInClick}
                        disabled={atMaxZoom}
                        aria-label={t('image_zoom_in', { defaultValue: 'Zoom in' })}
                    >
                        <ZoomIn size={22} strokeWidth={2} />
                    </button>
                    <button
                        type="button"
                        className={styles.zoomRailButton}
                        onClick={onZoomOutClick}
                        disabled={atMinZoom}
                        aria-label={t('image_zoom_out', { defaultValue: 'Zoom out' })}
                    >
                        <ZoomOut size={22} strokeWidth={2} />
                    </button>
                </div>

                <div className={styles.bottomStack}>
                    {canCarousel && (
                        <div className={styles.carouselBar}>
                            <button
                                type="button"
                                className={styles.carouselBarButton}
                                onClick={(ev) => {
                                    ev.stopPropagation();
                                    goPrev();
                                }}
                                aria-label={t('image_carousel_previous', { defaultValue: 'Previous image' })}
                            >
                                <ChevronLeft size={24} strokeWidth={2} />
                            </button>
                            <span className={styles.slideCounter} aria-live="polite">
                                {activeIndex + 1} / {slideCount}
                            </span>
                            <button
                                type="button"
                                className={styles.carouselBarButton}
                                onClick={(ev) => {
                                    ev.stopPropagation();
                                    goNext();
                                }}
                                aria-label={t('image_carousel_next', { defaultValue: 'Next image' })}
                            >
                                <ChevronRight size={24} strokeWidth={2} />
                            </button>
                        </div>
                    )}
                    <p className={styles.hint}>
                        {t('image_zoom_hint', {
                            defaultValue:
                                'Tap image to zoom. Use arrows or swipe to change images. Drag to pan when zoomed.',
                        })}
                    </p>
                </div>

                <div
                    ref={stageRef}
                    className={`${styles.stage} ${isGrabbing ? styles.grabbing : ''}`}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerCancel}
                    onWheel={onWheel}
                >
                    <div className={styles.translateLayer} style={translateStyle}>
                        <div className={styles.scaleLayer} style={scaleStyle}>
                            {resolvedSrc ? (
                                <img
                                    ref={imgRef}
                                    src={resolvedSrc}
                                    alt={alt}
                                    className={styles.image}
                                    draggable={false}
                                    onLoad={onImageLoad}
                                />
                            ) : (
                                <div className={styles.imagePlaceholder} />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};
